import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Text,
  Flex,
  SimpleGrid,
  Box,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue, // Import Input
} from '@chakra-ui/react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  SearchIcon,
} from '@chakra-ui/icons';

const ViewMoreModal = ({
  isOpen,
  onClose,
  items = [],
  title = 'View More',
  pageSize = 12,
  width = '600px',
  renderItem,
  extraHeader,
}) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const bg = useColorModeValue('white', 'navy.700');

  // Filter items based on search query
  const filteredItems = items.filter((item) =>
    typeof item === 'string'
      ? item.toLowerCase().includes(search.toLowerCase())
      : JSON.stringify(item).toLowerCase().includes(search.toLowerCase()),
  );
  const totalPages = Math.ceil(filteredItems.length / pageSize);

  const handlePrev = () => setPage((prev) => Math.max(prev - 1, 1));
  const handleNext = () => setPage((prev) => Math.min(prev + 1, totalPages));
  const paginatedItems = filteredItems.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );
  const inputBg = useColorModeValue('white', 'navy.600');
  const textColor = useColorModeValue('navy.700', 'white');
  const hoverBg = useColorModeValue('gray.200', 'navy.500');
  const focusBorderColor = useColorModeValue('navy.500', 'navy.300');
  const borderColor = useColorModeValue('gray.300', 'navy.600');

  // Reset to first page when search changes
  React.useEffect(() => {
    setPage(1);
  }, [search]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="rgba(0, 0, 0, 0.7)" />
      <ModalContent bg={bg} maxW={width}>
        <ModalHeader position="relative">
          <Flex align="center" w="100%">
            <Box flex="1">
              <Text>{title}</Text>
            </Box>
            <Flex flex="1" justify="center">
              <Box>{extraHeader}</Box>
            </Flex>
            <Box flex={1} mr={5}>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  borderRadius="16px"
                  bg={inputBg}
                  color={textColor}
                  borderColor={borderColor}
                  _focus={{
                    borderColor: focusBorderColor,
                    boxShadow: `0 0 0 1px ${focusBorderColor}`,
                  }}
                />
              </InputGroup>
            </Box>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {filteredItems.length === 0 ? (
            <Text color="gray.400">No items to display.</Text>
          ) : (
            <SimpleGrid columns={4} spacing={3}>
              {paginatedItems.map((item, idx) =>
                renderItem ? (
                  renderItem(item, idx)
                ) : (
                  <Box
                    key={idx}
                    p={3}
                    borderRadius="md"
                    textAlign="center"
                    fontSize="sm"
                    fontWeight="500"
                  >
                    {item}
                  </Box>
                ),
              )}
            </SimpleGrid>
          )}
        </ModalBody>
        <ModalFooter>
          <Flex w="100%" justify="space-between" align="center">
            <IconButton
              icon={<ChevronLeftIcon />}
              onClick={handlePrev}
              isDisabled={page === 1}
              size="sm"
              aria-label="Previous Page"
            />
            <Text fontSize="sm">
              Page {page} of {totalPages || 1}
            </Text>
            <IconButton
              icon={<ChevronRightIcon />}
              onClick={handleNext}
              isDisabled={page === totalPages || totalPages === 0}
              size="sm"
              aria-label="Next Page"
            />
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ViewMoreModal;
